import * as stylex from '@stylexjs/stylex'
import type { ReactNode } from 'react'

import { ButtonLink } from '../button'
import { colors, fonts, fontSizes, lineHeights, spacing } from '../tokens/tokens.stylex'

const styles = stylex.create({
  navigation: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing.space8,
    fontFamily: fonts.sans,
    fontSize: fontSizes.small,
    lineHeight: lineHeights.small,
    minWidth: 0,
    borderBlockEndWidth: 1,
    borderBlockEndStyle: 'solid',
    borderBlockEndColor: colors.hairline,
    paddingBlockEnd: spacing.space8,
  },
  list: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: spacing.space4,
    rowGap: spacing.space4,
    listStyle: 'none',
    margin: 0,
    padding: 0,
    flex: '0 1 auto',
  },
  actions: {
    display: 'flex',
    flex: '1 1 auto',
    minWidth: 0,
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: spacing.space8,
    marginInlineStart: 'auto',
  },
  inline: { borderBlockEndWidth: 0, paddingBlockEnd: 0 },
})

export type SectionNavigationItem = {
  readonly label: string
  readonly href: string
  readonly current: boolean
}

export type SectionNavigationProps = {
  readonly label: string
  readonly items: readonly SectionNavigationItem[]
  readonly actions?: ReactNode
  readonly variant?: 'default' | 'inline'
}

/**
 * Link related pages within a workspace in a compact, wrapping row. Choose NavigationMenu for site navigation or
 * SegmentedControl for a choice that changes a value. Applications supply destinations and the current page;
 * links retain native navigation and use an enclosing LinkProvider when supplied. Put occasional workspace tools
 * in actions; keep primary destinations in items. Choose inline when CollectionToolbar owns the surrounding row.
 * Status: supported.
 */
export function SectionNavigation({ label, items, actions, variant = 'default' }: SectionNavigationProps) {
  return (
    <nav aria-label={label} {...stylex.props(styles.navigation, variant === 'inline' && styles.inline)}>
      <ul {...stylex.props(styles.list)}>
        {items.map((item) => (
          <li key={item.href}>
            <ButtonLink size="sm" variant="tab" href={item.href} aria-current={item.current ? 'page' : undefined}>
              {item.label}
            </ButtonLink>
          </li>
        ))}
      </ul>
      {actions && <div {...stylex.props(styles.actions)}>{actions}</div>}
    </nav>
  )
}
